import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Heart, Package, Settings, LogOut, FolderHeart } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const InitialsCircle = ({ name, email, size = 32 }) => {
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : email ? email[0].toUpperCase() : '?';

  return (
    <span
      className="rounded-full flex items-center justify-center bg-gradient-to-br from-[#B49A88] to-[#B49A88] text-white font-serif font-bold select-none flex-shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

const UserNavMenu = ({ size = 34 }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (!user) return null;

  const avatarUrl = user.avatar_url
    ? `${BACKEND}${user.avatar_url}?_t=${Date.now()}`
    : user.google_picture || null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { label: 'My Profile', icon: User, action: () => navigate('/profile'), testId: 'menu-profile' },
    { label: 'My Favorites', icon: Heart, action: () => navigate('/favorites'), testId: 'menu-favorites' },
    { label: 'My Collections', icon: FolderHeart, action: () => navigate('/collections'), testId: 'menu-collections' },
    { label: 'My Products', icon: Package, action: () => navigate('/dashboard'), testId: 'menu-products' },
    { label: 'Settings', icon: Settings, action: () => navigate('/profile'), testId: 'menu-settings' },
  ];

  const isPro = user.subscription_tier === 'pro';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-nav-avatar"
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B49A88] focus-visible:ring-offset-2 transition-transform hover:scale-105 relative"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="rounded-full object-cover border-2 border-[#d8cfc8] hover:border-[#B49A88]/50 transition-colors"
              style={{ width: size, height: size }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <span style={{ display: avatarUrl ? 'none' : 'flex' }}>
            <InitialsCircle name={user.full_name} email={user.email} size={size} />
          </span>
          {isPro && (
            <span data-testid="pro-badge" className="absolute -top-1 -right-1 bg-[#8C7A66] text-white text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded-full shadow-sm leading-none">PRO</span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-56 rounded-xl border border-stone-100 bg-white shadow-lg p-1"
        data-testid="user-nav-menu"
      >
        {/* User info header */}
        <div className="px-3 py-2.5 mb-1">
          <p className="font-sans font-semibold text-stone-800 text-sm truncate" data-testid="menu-user-name">
            {user.full_name || user.email}
          </p>
          <p className="font-sans text-stone-400 text-xs truncate">{user.email}</p>
        </div>

        <DropdownMenuSeparator className="bg-stone-100" />

        {menuItems.map((item) => (
          <DropdownMenuItem
            key={item.testId}
            data-testid={item.testId}
            onClick={item.action}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-stone-600 hover:text-stone-900 font-sans text-sm focus:bg-stone-50"
          >
            <item.icon className="w-4 h-4 text-stone-400" />
            {item.label}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator className="bg-stone-100" />

        <DropdownMenuItem
          data-testid="menu-logout"
          onClick={handleLogout}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-red-500 hover:text-red-600 font-sans text-sm focus:bg-red-50"
        >
          <LogOut className="w-4 h-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { InitialsCircle };
export default UserNavMenu;
